import { apiClient } from '@/shared/lib/apiClient';
import type { ApiSuccessResponse } from '@super-admin/shared-types';

export type InstapayRecipientDetails = {
  recipient_name?: string;
  recipient_phone?: string;
  recipient_iban?: string;
};

export type PortalRenewalQuote = {
  quote_id: string;
  expires_at: string;
  currency: string;
  billing_period: string;
  tier_slug: string;
  lines: Array<{
    line_type: 'plan' | 'addon';
    slug: string;
    quantity: number;
    unit_amount_minor: number;
    line_total_minor: number;
  }>;
  subtotal_minor: number;
  discount_minor: number;
  total_minor: number;
  subscription_id: string;
  product_slug: string;
  product_name: string;
  plan_name: string;
};

export type PortalRenewalMyFatoorahResult = {
  payment_provider: 'myfatoorah';
  payment_id: string;
  payment_url: string;
  status: 'pending';
  amount_minor: number;
  currency: string;
  quote_id: string;
  checkout_session_id: string;
};

export type PortalRenewalInstapayResult = {
  payment_provider: 'instapay';
  payment_id: string;
  status: 'pending_review';
  review_status: 'pending_review';
  amount_minor: number;
  currency: string;
  quote_id: string;
  instapay_details: InstapayRecipientDetails;
};

export type PortalRenewalResult = PortalRenewalMyFatoorahResult | PortalRenewalInstapayResult;

export async function fetchPortalRenewalQuote(): Promise<PortalRenewalQuote> {
  const res = await apiClient.get<ApiSuccessResponse<PortalRenewalQuote>>(
    '/api/v1/customer/subscription/renewal-quote',
  );
  return res.data;
}

export async function initiatePortalRenewal(input: {
  paymentProvider: 'myfatoorah' | 'instapay';
  locale?: 'ar' | 'en';
  idempotencyKey: string;
}): Promise<PortalRenewalResult> {
  const res = await apiClient.post<ApiSuccessResponse<PortalRenewalResult>>(
    '/api/v1/customer/subscription/renew',
    {
      paymentProvider: input.paymentProvider,
      locale: input.locale,
    },
    {
      headers: { 'Idempotency-Key': input.idempotencyKey },
    },
  );
  return res.data;
}

export async function uploadPortalInstapayProof(paymentId: string, file: File): Promise<void> {
  const form = new FormData();
  form.append('receipt', file);
  await apiClient.postForm<ApiSuccessResponse<void>>(
    `/api/v1/customer/subscription/renew/${paymentId}/instapay-proof`,
    form,
    {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    },
  );
}
